// pfs-avatar.ts — generischer Avatar (PFS-600, Epic PFS-591).
//
// Loest das ueber die Cut-Apps duplizierte .avatar-Muster ab (mednet-identity
// Initialen-Block, sparkden-gps Karten-Avatare u. a.): runder/eckiger Avatar mit
// Namens-Initialen ODER Bild, optionalem Praesenz-Punkt, +N-Overflow-Chip.
//
// Farbe = benannte Naht (feedback_seams_not_flags): Default uniform
// --pfs-color-primary (deckt den Einzel-User-Fall + "weniger Farben, jede zum
// Layer"). Wo eine Domaene viele Personen unterscheiden muss (sparkden-gps-Karte),
// reicht SIE den Tint per data-color rein — das WC bleibt dummer Renderer, die
// "warum diese Farbe"-Logik (Hash o. ae.) bleibt in der Domaene (Ports & Adapters).
//
// Praezedenz des Inhalts: more > src > symbol > Initialen(name). Farbe/Status
// gelten fuer alle ausser more.
//
// Attribute (alle data-*):
//   data-name     Voller Name → Initialen (erster + letzter Wort-Anfangsbuchstabe)
//   data-src      Bild-URL (schlaegt symbol/Initialen)
//   data-symbol   Literales Glyph/Emoji als Marke (z. B. 🔥) STATT berechneter Initialen — fuer
//                 Brand-/Platzhalter-Avatare (sparkden-Flamme). Die "warum dieses Zeichen"-Wahl
//                 bleibt beim Consumer (dummer Renderer). data-src schlaegt es.
//   data-color    Flaechen-Tint (Naht; Default --pfs-color-primary). Bild/more ignorieren ihn.
//   data-size     sm | md (Default) | lg
//   data-shape    circle (Default) | square (abgerundetes Quadrat)
//   data-status   online | busy | away → farbiger Praesenz-Punkt; offline → Avatar ausgegraut
//   data-more     +N-Overflow-Chip (neutrale Flaeche, zeigt den Wert woertlich, z. B. "+3")
//
// CSS-Custom-Property-Naehte (fuer Faelle jenseits der data-size-Stufen):
//   --pfs-avatar-size   freie Groesse (ueberschreibt data-size), z. B. 1.6rem
//   --pfs-avatar-ring   optionaler box-shadow-Umriss (Overlap auf Karten), Default none
//
// Beispiel: <pfs-avatar data-name="Jürgen Südhoff" data-status="online"></pfs-avatar>
//           <pfs-avatar data-name="Ada Byron" data-color="hsl(210 52% 48%)"></pfs-avatar>
//           <pfs-avatar data-symbol="🔥" data-color="var(--pfs-color-primary)"></pfs-avatar>
//           <pfs-avatar data-more="+3"></pfs-avatar>
import { defineComponent } from "../../composition/define-component.js";
import { withShadow } from "../../composition/with-shadow.js";

/* Groesse als Naht: data-size gibt die Stufen-Defaults, --pfs-avatar-size
   ueberschreibt frei (z. B. sparkden-gps-Karte: 1.6rem). --pfs-avatar-ring
   = optionaler Umriss (Overlap auf Karten), Default keiner. */
const STYLES = `
:host { display: inline-block; --_s: var(--pfs-avatar-size, 2.5rem); }
:host([data-size="sm"]) { --_s: var(--pfs-avatar-size, 1.75rem); }
:host([data-size="lg"]) { --_s: var(--pfs-avatar-size, 3.5rem); }
.av {
  position: relative; box-sizing: border-box;
  width: var(--_s); height: var(--_s);
  border-radius: 9999px;
  background: var(--pfs-color-primary);
  color: var(--pfs-color-on-primary);
  display: flex; align-items: center; justify-content: center;
  font-family: var(--pfs-font-family, inherit);
  font-weight: 600; font-size: calc(var(--_s) * 0.4); line-height: 1;
  overflow: hidden; user-select: none;
  box-shadow: var(--pfs-avatar-ring, none);
}
:host([data-shape="square"]) .av { border-radius: var(--pfs-radius-md, 0.5rem); }
.av img { width: 100%; height: 100%; object-fit: cover; }
/* offline: ganzer Avatar ausgegraut (statt Punkt) — sparkden-gps-Muster */
.av.muted { filter: grayscale(0.7); opacity: 0.6; }
/* +N-Overflow-Chip: neutrale Flaeche, kein Tint */
.av.more { background: var(--pfs-color-outline-strong); color: var(--pfs-color-on-surface); }
.dot {
  position: absolute; right: 0; bottom: 0;
  width: calc(var(--_s) * 0.28); height: calc(var(--_s) * 0.28);
  border-radius: 9999px;
  border: 2px solid var(--pfs-color-surface);
  box-sizing: border-box;
}
.dot.online { background: var(--pfs-color-success); }
.dot.busy   { background: var(--pfs-color-danger); }
.dot.away   { background: var(--pfs-color-warning); }
`;

// Praesenz-Punkte nur fuer aktive Zustaende; offline wird als Ausgrauen gerendert.
const DOT_STATUSES = ["online", "busy", "away"];

function initials(name: string): string {
  const parts = (name ?? "").trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

defineComponent({
  tagName: "pfs-avatar",
  mixins: [withShadow()],
  render(host, ctx) {
    if (!ctx.shadowRoot) return;
    const root = ctx.shadowRoot;
    const style = document.createElement("style");
    style.textContent = STYLES;
    root.appendChild(style);

    const av = document.createElement("div");
    av.className = "av";
    root.appendChild(av);

    const paint = () => {
      // Jeder Paint baut .av frisch auf (reaktiv) — Klasse + Inline-Tint zuruecksetzen.
      av.className = "av";
      av.style.backgroundColor = "";
      av.textContent = "";

      // +N-Overflow-Chip: neutrale Flaeche, kein Bild/Initialen/Punkt/Tint.
      const more = host.dataset.more;
      if (more) {
        av.classList.add("more");
        av.textContent = more;
        return;
      }

      const color = host.dataset.color;
      if (color) av.style.backgroundColor = color;

      const src = host.dataset.src;
      const symbol = host.dataset.symbol;
      if (src) {
        const img = document.createElement("img");
        img.src = src;
        img.alt = host.dataset.name ?? "";
        av.appendChild(img);
      } else if (symbol) {
        // Literales Glyph/Emoji (Brand-Mark) statt berechneter Initialen.
        av.textContent = symbol;
      } else {
        av.textContent = initials(host.dataset.name ?? "");
      }

      const st = host.dataset.status;
      if (st === "offline") {
        av.classList.add("muted");
      } else if (st && DOT_STATUSES.includes(st)) {
        const dot = document.createElement("span");
        dot.className = "dot " + st;
        av.appendChild(dot);
      }
    };
    paint();
    const obs = new MutationObserver(paint);
    ctx.cleanup.push(() => obs.disconnect());
    obs.observe(host, {
      attributes: true,
      attributeFilter: ["data-name", "data-src", "data-symbol", "data-color", "data-status", "data-more"],
    });
  },
});
